import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Icon from "@ui-kit/icons/Icon.web";
import {
  TicketTypeSerializer,
  PublicBaseEventSerializer,
} from "@shared/modules/events/types/public";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Button from "@ui-kit/form/Button/Button.web";
import { useTranslation } from "@shared/utils/internationalization";
import ResponsiveDialog from "@shared/components/ui-kit/overlays/ResponsiveDialog/ResponsiveDialog.web";
import TicketTypesContainer from "@app-contexts/public/features/tickets/components/TicketTypesContainer/TicketTypesContainer.web";
import { PublicVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/venues";
import ParsedHtml from "@shared/components/ui-kit/typography/ParsedHtml/ParsedHtml.web";
import CardImage from "@shared/components/application/media/CardImage/CardImage.web";
import { getEventImageSource } from "@shared/utils/images/utils";
import { componentName } from "@shared/utils/bem";
import "./TicketTypeDetailModal.css";

export interface TicketTypeDetailModalProps {
  ticketType: TicketTypeSerializer;
  event: PublicBaseEventSerializer;
  venue: PublicVenueSerializer;
}

export const TicketTypeImage = ({
  ticketType,
  event,
}: {
  ticketType: TicketTypeSerializer;
  event?: PublicBaseEventSerializer;
}) => {
  return (
    <Flex
      overflow="hidden"
      // TODO: Add aspectRatio prop to Flex component
      // eslint-disable-next-line showpass-custom/prefer-semantic-props
      css={{ aspectRatio: "2" }}
      align="center"
      justify="center"
    >
      <CardImage
        width={200}
        height={100}
        src={ticketType.image ?? getEventImageSource(event)}
        alt={ticketType.name}
        title={ticketType.name}
        ariaLabel={ticketType.name}
        useServerlessResize
        aspectRatio="2"
        imageStyle={{
          maxHeight: "100%",
        }}
      />
    </Flex>
  );
};

export const TicketTypeDetailModal = ({
  ticketType,
  event,
  venue,
}: TicketTypeDetailModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation("common");
  const { name, description } = ticketType;
  const bem = componentName("ticket-type-detail-modal");

  const descriptionComponent = (
    <Box className={bem.element("description")}>
      {description && <ParsedHtml htmlString={description} />}
    </Box>
  );

  return (
    <>
      <Box className={bem.className()}>
        <Box className={bem.element("trigger")}>
          <Button.Link
            onClick={onOpen}
            visibility={description ? "visible" : "hidden"}
            data-testid="ticket-type-description-link"
          >
            <Icon name="Info" w="element.md" h="element.md" />
          </Button.Link>
        </Box>

        <ResponsiveDialog
          isOpen={isOpen}
          onClose={onClose}
          size="sm"
          headerComponent={name ?? t("Description")}
          bodyComponent={
            <Box className={bem.element("body")}>
              <Box borderRadius="sm" overflow="hidden" flexShrink={0}>
                <TicketTypeImage ticketType={ticketType} />
              </Box>

              <TicketTypesContainer
                eventData={event}
                venue={venue}
                tt={ticketType}
                isLoading={false}
                isRecurring={false}
                ticketsAuthorizedByPassword={[]}
                ticketAuthArray={[]}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                updateTicketAuthArray={() => {}}
                basketVoucherCode={null}
                userVouchers={[]}
                quantityButtonsAlwaysBelow
                hideDescription
                itemQuantitySelectInfoChildren={descriptionComponent}
              />
            </Box>
          }
        />
      </Box>
    </>
  );
};
